import React, { useState, useEffect } from "react";
import { User } from "@apis/index";
import { Search, Plus, UserPlus } from "lucide-react";
import Typography from "@components/typography";
import Fields from "@components/fields";
import Button from "@components/button";
import Row from "@components/row";
import { toast } from "react-toastify";
import { Formik, Form } from "formik";
import * as S from "../style";

interface CustomerSearchProps {
  onCustomerSelect: (customer: any) => void;
  selectedCustomer: any;
}

const CustomerSearch: React.FC<CustomerSearchProps> = ({
  onCustomerSelect,
  selectedCustomer,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [loading, setLoading] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    name: "",
    phoneNo: "+977",
    email: "",
  });

  useEffect(() => {
    const searchCustomers = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }
      try {
        setLoading(true);
        const res = await User.list({
          params: { role: "customer", search: query, limit: 5 },
        });
        setResults(res?.items || []);
      } catch (err) {
        console.error("Failed to search customers");
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleRegister = async (values: any) => {
    if (!values.name || !values.phoneNo) {
      toast.warn("Name and phone are required");
      return;
    }
    try {
      setLoading(true);
      const res = await User.createCustomer({ data: values });
      if (res) {
        onCustomerSelect(res);
        setShowRegister(false);
        setQuery("");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (selectedCustomer) {
    return (
      <S.Card style={{ marginBottom: "20px" }}>
        <Row styleRow="justify-content: space-between">
          <div>
            <Typography styleText="font-weight: 600; font-size: 16px;">
              {selectedCustomer.name}
            </Typography>
            <Typography styleText="color: #6B7280; font-size: 14px;">
              {selectedCustomer.phoneNo}{" "}
              {selectedCustomer.email ? `• ${selectedCustomer.email}` : ""}
            </Typography>
          </div>
          <Button
            variant="outline"
            onClick={() => onCustomerSelect(null)}
            title="Change"
          />
        </Row>
      </S.Card>
    );
  }

  return (
    <S.Card
      style={{
        marginBottom: "20px",
        position: "relative",
        overflow: "visible",
        zIndex: 10,
      }}
    >
      <Typography styleText="font-weight: 600; margin-bottom: 12px;">
        Customer Information
      </Typography>

      {!showRegister ? (
        <>
          <S.SearchWrapper style={{ marginBottom: 0 }}>
            <Search size={18} color="#9CA3AF" />
            <input
              placeholder="Search by name or phone..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
            />
            {query && (
              <Button
                variant="outline"
                onClick={() => {
                  const formattedPhone = query.match(/^[0-9]+$/)
                    ? query.startsWith("9")
                      ? `+977${query}`
                      : query.startsWith("+977")
                        ? query
                        : `+977${query}`
                    : "+977";
                  setNewCustomer({ ...newCustomer, phoneNo: formattedPhone });
                  setShowRegister(true);
                }}
                title="New"
                icon={<Plus size={16} style={{ marginRight: "4px" }} />}
              />
            )}
          </S.SearchWrapper>

          {showDropdown && query.length >= 2 && (
            <S.SearchDropdown>
              {loading ? (
                <div
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    color: "#6B7280",
                  }}
                >
                  Searching...
                </div>
              ) : results.length > 0 ? (
                results.map((c) => (
                  <S.DropdownItem
                    key={c.id}
                    onClick={() => {
                      onCustomerSelect(c);
                      setShowDropdown(false);
                      setQuery("");
                    }}
                  >
                    <Typography styleText="font-weight: 500">
                      {c.name}
                    </Typography>
                    <Typography styleText="font-size: 12px; color: #6B7280;">
                      {c.phoneNo}
                    </Typography>
                  </S.DropdownItem>
                ))
              ) : (
                <div style={{ padding: "16px", textAlign: "center" }}>
                  <Typography styleText="color: #6B7280; margin-bottom: 8px;">
                    No customer found matching "{query}"
                  </Typography>
                  <Button
                    onClick={() => {
                      const formattedPhone = query.match(/^[0-9]+$/)
                        ? query.startsWith("9")
                          ? `+977${query}`
                          : query.startsWith("+977")
                            ? query
                            : `+977${query}`
                        : "+977";
                      setNewCustomer({
                        ...newCustomer,
                        phoneNo: formattedPhone,
                      });
                      setShowRegister(true);
                    }}
                    title="Register New Customer"
                    icon={<UserPlus size={16} style={{ marginRight: "8px" }} />}
                  />
                </div>
              )}
            </S.SearchDropdown>
          )}
        </>
      ) : (
        <Formik
          initialValues={newCustomer}
          enableReinitialize
          onSubmit={handleRegister}
        >
          {({ values, setFieldValue }) => (
            <Form
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <Row gap="12px">
                <div style={{ flex: 1 }}>
                  <Fields.Input label="Full Name *" name="name" />
                </div>
              </Row>
              <Row gap="12px">
                <div style={{ flex: 1 }}>
                  <Fields.Input
                    label="Phone *"
                    name="phoneNo"
                    onChange={(val: string) => {
                      if (!val.startsWith("+977")) {
                        setFieldValue("phoneNo", "+977");
                      } else {
                        setFieldValue("phoneNo", val);
                      }
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Fields.Input
                    label="Email"
                    name="email"
                    type="email"
                  />
                </div>
              </Row>
              <Row
                styleRow="justify-content: flex-end; margin-top: 8px;"
                gap="12px"
              >
                <Button
                  variant="outline"
                  onClick={() => setShowRegister(false)}
                  title="Cancel"
                />
                <Button
                  type="submit"
                  title="Register & Select"
                  loading={loading}
                />
              </Row>
            </Form>
          )}
        </Formik>
      )}
    </S.Card>
  );
};

export default CustomerSearch;
